import { useMemo, useState } from 'react'
import { marketOptions, sortingOptions, timeWindowOptions } from '../constants/mockLeagueEvents'

const now = () => new Date()

const useEventFilters = (events, defaults) => {
  const [filters, setFilters] = useState(defaults)

  const updateFilters = (changes) => {
    setFilters((prev) => ({
      ...prev,
      ...changes,
    }))
  }

  const resetFilters = () => {
    setFilters({
      ...defaults,
      timeWindow: timeWindowOptions[0],
      market: marketOptions[0],
      sortOption: sortingOptions[0],
    })
  }

  const filteredEvents = useMemo(() => {
    const activeNow = now()
    const normalizedEvents = events
      .filter((event) => {
        if (!filters.leagueId) {
          return true
        }
        return event.leagueId.toLowerCase().includes(filters.leagueId.toLowerCase())
      })
      .filter((event) => {
        if (!filters.search) {
          return true
        }
        const query = filters.search.toLowerCase()
        return (
          event.leagueName.toLowerCase().includes(query) ||
          event.homeTeam.toLowerCase().includes(query) ||
          event.awayTeam.toLowerCase().includes(query)
        )
      })
      .filter((event) => {
        if (!filters.market || filters.market.id === 'all') {
          return true
        }
        return event.market === filters.market.id
      })
      .filter((event) => {
        if (!filters.timeWindow || filters.timeWindow.id === 'all') {
          return true
        }
        const minutes = Number(filters.timeWindow.id)
        const eventDate = new Date(event.eventTime)
        if (Number.isNaN(eventDate.valueOf())) {
          return false
        }
        if (eventDate < activeNow) {
          return false
        }
        const diff = (eventDate.getTime() - activeNow.getTime()) / (60 * 1000)
        return diff <= minutes
      })
    const sortId = filters.sortOption?.id ?? 'time-asc'

    const parseEventTime = (event) => {
      const source = event?.eventTime ?? event?.eventDate
      const date = new Date(source)
      if (Number.isNaN(date.valueOf())) {
        return null
      }
      return date.valueOf()
    }

    const sorted = [...normalizedEvents].sort((a, b) => {
      if (sortId === 'time-desc') {
        const timeA = parseEventTime(a)
        const timeB = parseEventTime(b)
        if (timeA === null && timeB === null) return 0
        if (timeA === null) return 1
        if (timeB === null) return -1
        return timeB - timeA
      }

      if (sortId === 'league-id') {
        const leagueA = a.leagueId?.toString()?.toLowerCase() ?? ''
        const leagueB = b.leagueId?.toString()?.toLowerCase() ?? ''
        return leagueA.localeCompare(leagueB)
      }

      const timeA = parseEventTime(a)
      const timeB = parseEventTime(b)
      if (timeA === null && timeB === null) return 0
      if (timeA === null) return 1
      if (timeB === null) return -1
      return timeA - timeB
    })

    return sorted
  }, [events, filters])

  return {
    filters,
    updateFilters,
    resetFilters,
    filteredEvents,
  }
}

export default useEventFilters
